import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from 'components';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';

// Custom tooltip to show engagement details
function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    return (
      <TooltipContainer>
        <TooltipLabel>{label}</TooltipLabel>
        <TooltipValue>Comments: {data.directComments}</TooltipValue>
        <TooltipValue>Replies: {data.associatedReplies}</TooltipValue>
        {/* <TooltipValue>Unique Users: {data.uniqueUsers}</TooltipValue> */}
        <TooltipValue style={{ fontWeight: 'bold', marginTop: '4px' }}>
          Total Engagement: {data.totalEngagement}
        </TooltipValue>
      </TooltipContainer>
    );
  }
  return null;
}

// Format function for total engagement legend
const formatTotalLegend = () => <LegendText>Total Engagement</LegendText>;

// Format function for breakdown legend
const formatBreakdownLegend = (value) => (
  <LegendText>
    {value === 'directComments'
      ? 'Comments'
      : value === 'associatedReplies'
        ? 'Replies'
        : value}
  </LegendText>
);

function QuestionPopularityChart({ data }) {
  const [viewMode, setViewMode] = useState('total'); // 'total', 'breakdown'

  // Show empty state if no data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <EmptyState>
        <EmptyMessage>No question engagement data available</EmptyMessage>
      </EmptyState>
    );
  }

  // Color scheme for different metrics
  const colors = {
    totalEngagement: '#3B82F6', // Blue
    directComments: '#10B981', // Green
    associatedReplies: '#F59E0B', // Orange
    uniqueUsers: '#8B5CF6' // Purple
  };

  const chartData = data.map((question) => ({
    name:
      question.title.length > 30
        ? `${question.title.substring(0, 30)}...`
        : question.title,
    fullTitle: question.title,
    directComments: question.directComments,
    associatedReplies: question.associatedReplies,
    uniqueUsers: question.uniqueUsers,
    totalEngagement: question.totalEngagement
  }));

  return (
    <Container>
      <Controls>
        <Button
          $variant={viewMode === 'total' ? 'contained' : 'outlined'}
          backgroundColor={viewMode === 'total' ? 'var(--blue)' : 'transparent'}
          textColor={viewMode === 'total' ? 'white' : 'var(--blue)'}
          type="button"
          onClick={() => setViewMode('total')}>
          Total Engagement
        </Button>
        <Button
          $variant={viewMode === 'breakdown' ? 'contained' : 'outlined'}
          backgroundColor={
            viewMode === 'breakdown' ? 'var(--blue)' : 'transparent'
          }
          textColor={viewMode === 'breakdown' ? 'white' : 'var(--blue)'}
          type="button"
          onClick={() => setViewMode('breakdown')}>
          Detailed Breakdown
        </Button>
      </Controls>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 60
          }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: '#666', dy: 5 }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#666' }}
            label={{
              value: 'Engagement',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: '#666', fontSize: '12px' }
            }}
          />
          <Tooltip content={<CustomTooltip />} />

          {viewMode === 'total' ? (
            <>
              <Bar
                dataKey="totalEngagement"
                fill={colors.totalEngagement}
                radius={[4, 4, 0, 0]}
              />
              <Legend
                verticalAlign="top"
                height={36}
                formatter={formatTotalLegend}
              />
            </>
          ) : (
            <>
              <Bar
                dataKey="directComments"
                stackId="a"
                fill={colors.directComments}
              />
              <Bar
                dataKey="associatedReplies"
                stackId="a"
                fill={colors.associatedReplies}
              />
              <Legend
                verticalAlign="top"
                height={36}
                formatter={formatBreakdownLegend}
              />
            </>
          )}
        </BarChart>
      </ResponsiveContainer>
    </Container>
  );
}

const Container = styled.div`
  padding: 1rem 0 0;
  width: 100%;

  .recharts-default-legend {
    display: flex;
    flex-direction: row-reverse;
    gap: 1rem;
    justify-content: center;
  }

  .recharts-legend-item {
    margin-right: 0 !important;
  }

  .recharts-legend-item:first-child {
    margin-right: 0 !important;
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 1rem;

  @media (max-width: 767px) {
    gap: 0.5rem;

    button {
      font-size: 0.85rem;
      padding: 0.5rem 1rem;
    }
  }

  @media (max-width: 419px) {
    gap: 0.375rem;

    button {
      font-size: 0.75rem;
      padding: 0.375rem 0.75rem;
    }
  }
`;

const EmptyState = styled.div`
  align-items: center;
  background: #f9f9f9;
  border-radius: 8px;
  display: flex;
  height: 300px;
  justify-content: center;
`;

const EmptyMessage = styled.p`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin: 0;
`;

const TooltipContainer = styled.div`
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
`;

const TooltipLabel = styled.div`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const TooltipValue = styled.div`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  margin: 0.25rem 0;
`;

const LegendText = styled.span`
  color: var(--brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
`;

export default QuestionPopularityChart;
